import Moment from 'moment';
import { Temporal } from "@js-temporal/polyfill";

function formatLocalDate(date: Moment.Moment | Date | string): string {
    if(date instanceof Date) {
        //It turns out that Chrome and Firefox set navigator.language to en-US
        //if you download the US version, completely ignoring your actual
        //machine locale. Internet Explorer and Edge do the correct thing.
        //PhantomJS uses US format, even for toLocaleDateString("en-NZ"). To
        //work around this issue, hard-code NZ date format.
        const today = Moment().startOf('day');
        const startOfDay = Moment(date).startOf('day');
        if(startOfDay.isSame(today)) {
            return "today";
        }
        return startOfDay.format('D MMMM YYYY');
    }

    if(typeof(date) === "string") {
        return formatLocalDate(Moment(date));
    }

    if(date instanceof Moment) {
        return formatLocalDate(date.toDate());
    }

    throw "Unable to format date due to unrecognised format.";
}

// The polyfilled Temporal API is slow to calculate the current date, so let's
// cache it. When the supported browsers support Temporal natively, we should
// hopefully be able to remove this cache and use Temporal directly.
//
// Keep an eye on https://caniuse.com/temporal.
//
function getTodayAsTemporalDateCachedBecausePolyfillIsSlow(): Temporal.PlainDate {
    const today = new Date().toISOString().split("T")[0];
    const cache = getTodayAsTemporalDateCachedBecausePolyfillIsSlow.cache;
    if(cache.lastCalculated === undefined ||
        cache.lastResult === undefined ||
        cache.lastCalculated !== today)
    {
        cache.lastResult = Temporal.Now.plainDateISO();
        cache.lastCalculated = today;
    }

    return cache.lastResult;
}

getTodayAsTemporalDateCachedBecausePolyfillIsSlow.cache = {
    lastCalculated: undefined as string | undefined,
    lastResult: undefined as Temporal.PlainDate | undefined
};

const nzLocaleForTemporalFormatting = "en-NZ";
const nzLocaleOptionsForTemporalFormatting: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
};

function getLocalTimeZoneId() {
    return Temporal.Now.timeZoneId();
}

// The NZ locale is only being used to format the instant value, the instant itself is not being converted to NZ local time.
function formatInstantUsingNzDateTimeFormat(instant: Temporal.Instant) {
    const localTimeZoneId = getLocalTimeZoneId();
    return instant.toZonedDateTimeISO(localTimeZoneId)
        .toLocaleString(nzLocaleForTemporalFormatting, nzLocaleOptionsForTemporalFormatting);
}

function getTimeZoneName(time: Temporal.ZonedDateTime) {
    const timeZoneId = time.timeZoneId;
    const formatter = new Intl.DateTimeFormat(undefined, { timeZoneName: "shortGeneric", timeZone: timeZoneId });
    const parts = formatter.formatToParts(time.epochMilliseconds);
    return parts.find(p => p.type === "timeZoneName")?.value;
}

export {
    formatLocalDate,
    getTodayAsTemporalDateCachedBecausePolyfillIsSlow,
    formatInstantUsingNzDateTimeFormat,
    getTimeZoneName
};

export const exportedForTesting = {
    getLocalTimeZoneId
};
